import { ApiError } from '@/types/api';
import { apiConfig } from '@/config/api';
import {
  ACCOUNT_TOKEN_KEY,
  CUSTOMER_TOKEN_KEY,
  getAuthItem,
  removeAuthItem,
  setAuthItem,
} from '@/lib/auth-storage';
import { getErrorMessage } from '@/lib/error-codes';

export const HTTP_CONFIG = {
  DEFAULT_HEADERS: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  RETRY_DELAYS: [1000, 2000, 4000],
} as const;

export { ACCOUNT_TOKEN_KEY, CUSTOMER_TOKEN_KEY };

type AuthScope = 'account' | 'customer';

export class HttpClient {
  private baseUrl: string;
  private timeout: number;
  private retries: number;

  constructor() {
    this.baseUrl = apiConfig.baseUrl;
    this.timeout = apiConfig.timeout || 10000;
    this.retries = apiConfig.retries || 3;
  }

  private resolveAuthScope(endpoint: string, includeAuth: boolean): AuthScope | null {
    if (!includeAuth) {
      return null;
    }

    if (endpoint.startsWith('/accounts/') || endpoint.startsWith('/account/')) {
      return 'account';
    }

    if (endpoint.startsWith('/rest/')) {
      return null;
    }

    return 'customer';
  }

  private getTokenForScope(scope: AuthScope | null): string | null {
    if (!scope) {
      return null;
    }

    return getAuthItem(scope === 'account' ? ACCOUNT_TOKEN_KEY : CUSTOMER_TOKEN_KEY);
  }

  public setAccountToken(token: string): void {
    setAuthItem(ACCOUNT_TOKEN_KEY, token);
  }

  public setCustomerToken(token: string): void {
    setAuthItem(CUSTOMER_TOKEN_KEY, token);
  }

  public removeAccountToken(): void {
    removeAuthItem(ACCOUNT_TOKEN_KEY);
  }

  public removeCustomerToken(): void {
    removeAuthItem(CUSTOMER_TOKEN_KEY);
  }

  public clearTokens(): void {
    this.removeAccountToken();
    this.removeCustomerToken();
  }

  public getBaseUrl(): string {
    return this.baseUrl;
  }

  private createHeaders(endpoint: string, includeAuth: boolean = true): HeadersInit {
    const headers: HeadersInit = { ...HTTP_CONFIG.DEFAULT_HEADERS };
    const scope = this.resolveAuthScope(endpoint, includeAuth);
    const token = this.getTokenForScope(scope);

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  private clearTokenForScope(scope: AuthScope | null): void {
    if (scope === 'account') {
      this.removeAccountToken();
      return;
    }

    if (scope === 'customer') {
      this.removeCustomerToken();
    }
  }

  private async handleResponse<T>(response: Response, authScope: AuthScope | null): Promise<T> {
    if (!response.ok) {
      let errorMessage = `HTTP Error: ${response.status}`;
      let errorDetails = null;
      let standardizedCode: string | undefined;

      try {
        const errorData = await response.json();
        errorDetails = errorData;

        if (errorData.type) {
          standardizedCode = errorData.type;
          errorMessage = getErrorMessage(errorData.type, errorData.message, errorData.payload);
        } else {
          errorMessage = errorData.message || errorMessage;
        }
      } catch {
        errorMessage = response.statusText || errorMessage;
      }

      const apiError: ApiError = {
        message: errorMessage,
        code: response.status.toString(),
        details: errorDetails,
        standardizedCode,
      };

      if (response.status === 401) {
        this.clearTokenForScope(authScope);
      }

      throw apiError;
    }

    if (response.status === 204) {
      return {} as T;
    }

    try {
      return await response.json();
    } catch {
      throw new Error('Ошибка при парсинге ответа сервера');
    }
  }

  private async executeRequest<T>(
    endpoint: string,
    url: string,
    options: RequestInit,
    includeAuth: boolean,
    attempt: number = 1
  ): Promise<T> {
    try {
      const authScope = this.resolveAuthScope(endpoint, includeAuth);

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeout);

      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      return await this.handleResponse<T>(response, authScope);
    } catch (error: any) {
      if (attempt >= this.retries || (error && error.code)) {
        throw error;
      }

      const delay = HTTP_CONFIG.RETRY_DELAYS[attempt - 1] || 1000;
      await new Promise(resolve => setTimeout(resolve, delay));

      return this.executeRequest<T>(endpoint, url, options, includeAuth, attempt + 1);
    }
  }

  async get<T>(endpoint: string, includeAuth: boolean = true): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const options: RequestInit = {
      method: 'GET',
      headers: this.createHeaders(endpoint, includeAuth),
    };

    return this.executeRequest<T>(endpoint, url, options, includeAuth);
  }

  async post<T, D = any>(
    endpoint: string,
    data?: D,
    includeAuth: boolean = true
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const options: RequestInit = {
      method: 'POST',
      headers: this.createHeaders(endpoint, includeAuth),
      body: data ? JSON.stringify(data) : undefined,
    };

    return this.executeRequest<T>(endpoint, url, options, includeAuth);
  }

  async put<T, D = any>(
    endpoint: string,
    data?: D,
    includeAuth: boolean = true
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const options: RequestInit = {
      method: 'PUT',
      headers: this.createHeaders(endpoint, includeAuth),
      body: data ? JSON.stringify(data) : undefined,
    };

    return this.executeRequest<T>(endpoint, url, options, includeAuth);
  }

  async delete<T, D = any>(
    endpoint: string,
    data?: D,
    includeAuth: boolean = true
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const options: RequestInit = {
      method: 'DELETE',
      headers: this.createHeaders(endpoint, includeAuth),
      body: data ? JSON.stringify(data) : undefined,
    };

    return this.executeRequest<T>(endpoint, url, options, includeAuth);
  }
}

export const httpClient = new HttpClient();
